<template>
  <div class="clever-management-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="heroicons:sparkles" width="24" height="24" />
        智能体管理
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="searchForm.coze_name" placeholder="请输入名称" clearable size="large" class="search-input"
            @keyup.enter="handleSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="handleSearch" class="search-btn">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>
        </div>

        <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
          <Icon icon="heroicons:plus" width="18" height="18" />
          <span class="btn-text">新增智能体</span>
        </el-button>
      </div>

      <!-- 表格包装器 -->
      <div class="table-wrapper">
        <el-table :data="cozeList" stripe v-loading="loading" class="clever-table"
          :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">
          <el-table-column prop="coze_name" label="名称" width="180" />
          <el-table-column label="类型" width="100">
            <template #default="scope">
              <el-tag :type="getTypeByUrl(scope.row.coze_url) === 'workflow' ? 'success' : 'primary'">
                {{ getTypeByUrl(scope.row.coze_url) === 'workflow' ? '工作流' : '智能体' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="coze_botid" label="ID" width="180" />
          <el-table-column prop="coze_url" label="接口地址" min-width="200" show-overflow-tooltip />
          <el-table-column prop="notes" label="备注" width="150" />
          <el-table-column prop="create_time" label="创建时间" width="180" />
          <el-table-column prop="update_time" label="更新时间" width="180" />

          <el-table-column label="操作" width="270" fixed="right">
            <template #default="scope">
              <button class="action-btn edit-btn" @click="openEditDialog(scope.row)">
                <Icon icon="heroicons:pencil-square" width="14" height="14" />
                <span class="btn-text">编辑</span>
              </button>
              <button class="action-btn test-btn" @click="testConnectivity(scope.row)">
                <Icon icon="heroicons:signal" width="14" height="14" />
                <span class="btn-text">测试</span>
              </button>
              <button class="action-btn delete-btn" @click="handleDelete(scope.row.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                <span class="btn-text">删除</span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" :layout="paginationLayout"
          @size-change="fetchCozeList" @current-change="fetchCozeList" background />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" :width="dialogWidth" :close-on-click-modal="false"
      class="custom-dialog" :top="dialogTop">
      <div class="api-tip-box">
        <el-alert title="还没有coze接口密钥？" type="info" :closable="false" show-icon center>
          <template #default>
            <a href="https://www.coze.cn/home" target="_blank" class="api-link">
              立即申请coze接口
            </a>
          </template>
        </el-alert>
      </div>
      <el-form ref="cozeForm" :model="cozeForm" :label-width="formLabelWidth" :rules="formRules">
        <el-form-item label="类型选择" prop="type">
          <el-select v-model="cozeForm.type" placeholder="请选择类型" size="large" style="width: 100%"
            @change="handleTypeChange">
            <el-option label="智能体" value="agent" />
            <el-option label="工作流" value="workflow" />
          </el-select>
        </el-form-item>

        <el-form-item :label="cozeForm.type === 'agent' ? '智能体名称' : '工作流名称'" prop="coze_name">
          <el-input v-model="cozeForm.coze_name" :placeholder="cozeForm.type === 'agent' ? '请输入智能体名称' : '请输入工作流名称'"
            size="large" />
        </el-form-item>

        <el-form-item :label="cozeForm.type === 'agent' ? '智能体密钥' : '工作流密钥'" prop="coze_access_token">
          <el-input v-model="cozeForm.coze_access_token"
            :placeholder="cozeForm.type === 'agent' ? '请输入智能体密钥' : '请输入工作流密钥'" size="large" show-password />
        </el-form-item>

        <el-form-item :label="cozeForm.type === 'agent' ? '智能体ID' : '工作流ID'" prop="coze_botid">
          <el-input v-model="cozeForm.coze_botid" :placeholder="cozeForm.type === 'agent' ? '请输入智能体ID' : '请输入工作流ID'"
            size="large" />
        </el-form-item>

        <el-form-item label="接口地址" prop="coze_url">
          <el-input v-model="cozeForm.coze_url" placeholder="请输入Coze接口地址" size="large" @input="handleUrlInput" />
          <div class="form-tip" v-if="showUrlTip">
            {{ urlTip }}
          </div>
        </el-form-item>

        <el-form-item label="测试连通性">
          <el-button type="primary" size="large" @click="testConnection" :loading="testingConnection"
            :disabled="!cozeForm.coze_url || !cozeForm.coze_access_token || !cozeForm.coze_botid">
            <Icon icon="heroicons:signal" width="18" height="18" />
            测试连通性
          </el-button>
          <span class="test-result" :class="{ 'success': testResult === 'success', 'error': testResult === 'error' }">
            {{ testResult === 'success' ? '✓ 测试通过' : testResult === 'error' ? '✗ 测试失败' : '' }}
          </span>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="cozeForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" @click="submitCozeForm">确认</button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Icon } from '@iconify/vue';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'SystemClever',
  components: { Icon },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      testingConnection: false,
      testResult: '',
      dialogTitle: '新增智能体',
      isEditMode: false,
      searchForm: { coze_name: '' },
      cozeForm: {
        id: null,
        type: 'agent',
        coze_name: '',
        coze_url: 'https://api.coze.cn/v3/chat',
        coze_access_token: '',
        coze_botid: '',
        notes: ''
      },
      isUrlModified: false,
      DEFAULT_URLS: {
        agent: 'https://api.coze.cn/v3/chat',
        workflow: 'https://api.coze.cn/v1/workflow/run'
      },
      formRules: {
        coze_name: [{ required: true, message: '请输入智能体名称', trigger: 'blur' }],
        coze_url: [{ required: true, message: '请输入接口地址', trigger: 'blur' }],
        coze_access_token: [{ required: true, message: '请输入智能体密钥', trigger: 'blur' }],
        coze_botid: [{ required: true, message: '请输入智能体ID', trigger: 'blur' }]
      },
      cozeList: [],
      pagination: { page: 1, pageSize: 10, total: 0 },
      screenWidth: window.innerWidth
    }
  },
  computed: {
    userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },
    urlTip() { return this.cozeForm.type === 'agent' ? '智能体默认接口地址' : '工作流默认接口地址'; },
    showUrlTip() { return !this.isUrlModified && this.cozeForm.coze_url === this.DEFAULT_URLS[this.cozeForm.type]; },
    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next'
      if (this.screenWidth < 768) return 'total, prev, pager, next'
      return 'total, sizes, prev, pager, next, jumper'
    },
    dialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '600px'
    },
    dialogTop() {
      if (this.screenWidth < 576) return '5vh'
      return '10vh'
    },
    formLabelWidth() {
      if (this.screenWidth < 576) return '80px'
      return '120px'
    }
  },
  mounted() {
    this.fetchCozeList();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() { this.screenWidth = window.innerWidth },
    async testConnectivity(row) {
      try {
        this.loading = true;
        if (this.getTypeByUrl(row.coze_url) === 'workflow') {
          ElMessage.warning('工作流暂时无法测试连通性！');
          return;
        } else {
          await this.testAgentConnection(row);
        }
      } catch (error) {
        console.error('测试连接失败:', error);
        ElMessage.error('测试连接失败: ' + (error.response?.data?.msg || error.message));
      } finally {
        this.loading = false;
      }
    },
    async testConnection() {
      try {
        this.testingConnection = true;
        this.testResult = '';
        if (this.cozeForm.type === 'workflow') {
          ElMessage.warning('工作流暂时无法测试连通性！');
          return;
        } else {
          await this.testAgentConnection(this.cozeForm);
        }
        this.testResult = 'success';
        ElMessage.success('连接测试成功！');
      } catch (error) {
        console.error('测试连接失败:', error);
        this.testResult = 'error';
        ElMessage.error('测试连接失败: ' + (error.response?.data?.msg || error.message));
      } finally {
        this.testingConnection = false;
      }
    },
    async testAgentConnection(row) {
      const testData = {
        bot_id: row.coze_botid,
        user_id: "test_user_" + Date.now(),
        stream: false,
        auto_save_history: true,
        additional_messages: [{ role: "user", content: "你好，这是一条测试消息", content_type: "text" }]
      };
      const response = await axios.post(row.coze_url, testData, {
        headers: { 'Authorization': `Bearer ${row.coze_access_token}`, 'Content-Type': 'application/json' }
      });
      if (response.data.code === 0) {
        ElMessage.success('智能体连接测试成功！');
      } else {
        throw new Error(response.data.msg || '未知错误');
      }
    },
    getTypeByUrl(url) {
      if (!url) return 'agent';
      return url.includes('workflow') ? 'workflow' : 'agent';
    },
    async fetchCozeList() {
      this.loading = true;
      try {
        const params = { action: 'list', store_id: this.userInfo.id, page: this.pagination.page, page_size: this.pagination.pageSize };
        if (this.searchForm.coze_name) params.coze_name = this.searchForm.coze_name;
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/CozeSettingsController.php`, params);
        if (data.data) { this.cozeList = data.data; this.pagination.total = data.pagination.total; }
      } catch (error) { ElMessage.error('获取智能体列表失败'); }
      finally { this.loading = false; }
    },
    handleSearch() { this.pagination.page = 1; this.fetchCozeList(); },
    handleTypeChange() {
      if (!this.isUrlModified) { this.cozeForm.coze_url = this.DEFAULT_URLS[this.cozeForm.type]; }
      this.$nextTick(() => { if (this.$refs.cozeForm) this.$refs.cozeForm.clearValidate(); });
    },
    handleUrlInput() { this.isUrlModified = true; },
    openAddDialog() {
      this.dialogTitle = '新增智能体'; this.isEditMode = false;
      this.cozeForm = { id: null, type: 'agent', coze_name: '', coze_url: this.DEFAULT_URLS.agent, coze_access_token: '', coze_botid: '', notes: '' };
      this.isUrlModified = false;
      this.dialogVisible = true;
    },
    openEditDialog(row) {
      const type = this.getTypeByUrl(row.coze_url);
      this.dialogTitle = type === 'agent' ? '编辑智能体' : '编辑工作流';
      this.isEditMode = true;
      this.cozeForm = { id: row.id, type: type, coze_name: row.coze_name, coze_url: row.coze_url, coze_access_token: row.coze_access_token, coze_botid: row.coze_botid, notes: row.notes };
      const defaultUrl = this.DEFAULT_URLS[type];
      this.isUrlModified = row.coze_url !== defaultUrl;
      this.dialogVisible = true;
    },
    submitCozeForm() {
      this.$refs.cozeForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = { ...this.cozeForm, store_id: this.userInfo.id };
            delete payload.type;
            const { data } = await axios.post(`${config.BASE_URL}/shop/api/CozeSettingsController.php`, { action, ...payload });
            if (data.success) { ElMessage.success(this.isEditMode ? '更新成功' : '添加成功'); this.dialogVisible = false; this.fetchCozeList(); }
          } catch (error) { ElMessage.error('操作失败'); }
        }
      });
    },
    async handleDelete(id) {
      try {
        await ElMessageBox.confirm('确定要删除该智能体吗？', '警告', { confirmButtonText: '确认删除', cancelButtonText: '取消', type: 'warning' });
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/CozeSettingsController.php`, { action: 'delete', id: id, store_id: this.userInfo.id });
        if (data.success) { ElMessage.success('删除成功'); this.fetchCozeList(); }
      } catch (error) { if (error !== 'cancel') ElMessage.error('删除失败'); }
    }
  }
}
</script>

<style scoped lang="scss">
.clever-management-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .search-left {
      width: 100%;
    }

    .search-left .el-input {
      width: 100% !important;
    }

    .search-left .search-btn {
      width: 100%;
    }

    .add-btn {
      width: 100%;
    }
  }
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(157, 78, 221, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(157, 78, 221, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.6);
    }
  }
}

:deep(.el-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;
  background-color: transparent !important;
  color: #e0e0e0;

  &::before {
    display: none;
  }

  .el-table__header-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      th:not(.el-table-fixed-column--right) {
        background: rgba(157, 78, 221, 0.2) !important;
        color: #fff !important;
        font-weight: 600;
        border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

        .cell {
          color: #fff !important;
        }
      }
    }
  }

  .el-table__body-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      td:not(.el-table-fixed-column--right) {
        background-color: transparent !important;
        border-bottom: 1px solid rgba(157, 78, 221, 0.1);
        color: #e0e0e0;
      }

      &.el-table__row--striped td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.05) !important;
      }

      &:hover td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.1) !important;
      }
    }
  }

  .el-table-fixed-column--right {
    background-color: #120e21 !important;
    backdrop-filter: none !important;
  }

  th.el-table-fixed-column--right {
    background-color: #2a1f45 !important;
  }

  .el-table__row--striped .el-table-fixed-column--right {
    background-color: #1a1430 !important;
  }

  .el-table__empty-block {
    background-color: transparent !important;

    .el-table__empty-text {
      color: #a0a0c0 !important;
    }
  }
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  margin: 0 4px;

  &.edit-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #c084fc;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
    }
  }

  &.test-btn {
    background: rgba(34, 197, 94, 0.15);
    border: 1px solid rgba(34, 197, 94, 0.4);
    color: #4ade80;

    &:hover {
      background: rgba(34, 197, 94, 0.25);
    }
  }

  &.delete-btn {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #f87171;

    &:hover {
      background: rgba(239, 68, 68, 0.25);
    }
  }

  @media (max-width: 576px) {
    .btn-text {
      display: none;
    }
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }
}

:deep(.custom-dialog) {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
  --el-dialog-border-radius: 20px;

  .el-dialog__header {
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;

    .el-dialog__title {
      color: #fff;
      font-weight: 600;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid rgba(157, 78, 221, 0.2);
    padding: 16px 24px;
  }
}

:deep(.el-form-item__label) {
  color: #a0a0c0;
  font-weight: 500;
}

:deep(.el-input__wrapper) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  box-shadow: none;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &.is-focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }

  .el-input__inner {
    color: #fff;
    background: transparent;

    &::placeholder {
      color: #5a5a7a;
    }
  }
}

:deep(.el-textarea__inner) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  color: #fff;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &:focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }
}

.api-tip-box {
  margin-bottom: 20px;

  :deep(.el-alert) {
    background-color: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
  }

  .api-link {
    display: inline-block;
    margin-left: 8px;
    font-weight: 600;
    color: #60a5fa;
    text-decoration: none;
    transition: all 0.3s;
    padding: 4px 8px;
    border-radius: 4px;

    &:hover {
      color: #93c5fd;
      background-color: rgba(96, 165, 250, 0.1);
      text-decoration: underline;
    }

    &::after {
      content: "↗";
      margin-left: 5px;
      font-size: 0.9em;
    }
  }
}

.form-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #4ade80;
  padding: 4px 8px;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 4px;
  display: inline-block;
}

.test-result {
  margin-left: 15px;
  font-size: 14px;

  &.success {
    color: #4ade80;
  }

  &.error {
    color: #f87171;
  }
}

.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;

  &.cancel-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      color: #c084fc;
    }
  }

  &.confirm-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
    }
  }
}

:deep(.el-button) {
  border-radius: 10px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

:deep(.el-button--primary) {
  background: linear-gradient(135deg, #6546b4, #9d4edd);
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
}

:deep(.el-button--success) {
  background: linear-gradient(135deg, #10b981, #34d399);
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
}

@media (max-width: 480px) {
  .btn-text {
    display: none;
  }
}
</style>

<!-- 非 scoped 全局样式 -->
<style lang="scss">
.el-dialog {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98) !important;
}

body>.el-overlay:has(.custom-dialog) .el-dialog__body {
  background: rgba(18, 14, 33, 0.98) !important;
}
</style>